import React, { useRef } from "react";
import { Bar } from "react-chartjs-2";
import { Chart as ChartJS, registerables } from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

ChartJS.register(...registerables);

const utahData = "/img/headerlogo.png";
const kpi = "/img/KPI-DASH 2.png";
const bodex = "/img/bodexLogo.png";
const pdf = "/img/pdf1.png";

const EngTable18 = ({ data }) => {
  const pdfRef = useRef();

  // ---- 📌 Prepare Dataset ----
  const processed = data.map((row) => {
    const growth = row.year_2024 - row.year_2018;
    const growthPct = ((growth / row.year_2018) * 100).toFixed(2);

    return { ...row, growth, growthPct };
  });

  // ---- 📌 Insights ----
  const topCategory = processed.reduce((a, b) =>
    Number(a.growthPct) > Number(b.growthPct) ? a : b
  );

  const avgGrowth =
    processed.reduce((sum, r) => sum + Number(r.growthPct), 0) /
    processed.length;

  // ---- 📊 Chart Dataset ----
  const chartData = {
    labels: processed.map((i) => i.education_label),
    datasets: [
      {
        label: "2018",
        backgroundColor: "#F6CFFF",
        borderRadius: 6,
        data: processed.map((i) => parseInt(i.year_2018)),
      },
      {
        label: "2024",
        backgroundColor: "#2C92B8",
        borderRadius: 6,
        data: processed.map((i) => parseInt(i.year_2024)),
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "Employment by Education Level (2018–2024)",
        font: { size: 18, weight: "bold" },
      },
    },
    scales: {
      y: { title: { display: true, text: "Employment Count" } },
      x: { title: { display: true, text: "Education Level" } },
    },
  };

  // ---- 📄 Download PDF ----
  const downloadPDF = () => {
    const element = pdfRef.current;

    html2canvas(element, { scale: 2 }).then((canvas) => {
      const pdf = new jsPDF("p", "mm", "a4");
      const width = pdf.internal.pageSize.getWidth();
      const height = (canvas.height * width) / canvas.width;

      // Header
      pdf.addImage(utahData, "PNG", 10, 6, 30, 25);
      pdf.setFontSize(20);
      pdf.text("UTahData.org", 50, 20);
      pdf.line(10, 34, 200, 34);

      // Content
      pdf.addImage(
        canvas.toDataURL("image/png"),
        "PNG",
        5,
        38,
        width - 10,
        height
      );

      // Footer
      pdf.line(10, 265, 200, 265);
      pdf.setFontSize(10);
      pdf.text(`Exported: 20250916.v3.198`, 145, 290);
      pdf.text(`Source: ${window.location.href}`, 10, 290);

      pdf.addImage(bodex, "PNG", 15, 268, 35, 10);
      pdf.addImage(kpi, "PNG", 60, 268, 15, 15);

      pdf.save("UtahData_Education_Employment_2018_2024.pdf");
    });
  };

  return (
    <>
      <div
        ref={pdfRef}
        className="p-6 bg-white rounded-xl shadow-md border border-gray-200"
      >
        <h2 className="text-center text-2xl font-bold text-gray-800 mb-6">
          Employment by Education Level (2018–2024)
        </h2>

        {/* ---- KPI Insights ---- */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-blue-100 p-4 text-center rounded-lg">
            <p className="font-semibold text-blue-800">📈 Average Growth</p>
            <p className="text-xl font-bold text-blue-700">
              {avgGrowth.toFixed(2)}%
            </p>
          </div>

          <div className="bg-green-100 p-4 text-center rounded-lg">
            <p className="font-semibold text-green-800">🚀 Fastest Growing</p>
            <p className="font-bold">{topCategory.education_label}</p>
            <p className="text-lg font-bold text-green-600">
              {topCategory.growthPct}%
            </p>
          </div>

          <div className="bg-gray-100 p-4 text-center rounded-lg">
            <p className="font-semibold text-gray-700">📁 Total Categories</p>
            <p className="text-xl font-bold">{processed.length}</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* ---- Data Table ---- */}
          <div className="flex flex-col justify-center">
            <div className="overflow-x-auto mb-6">
              <table className="w-full text-left shadow-md border rounded-lg overflow-hidden">
                <thead className="bg-gray-900 text-white">
                  <tr>
                    <th className="p-2">Education Level</th>
                    <th>2018</th>
                    <th>2024</th>
                    <th>Growth</th>
                    <th>% Change</th>
                  </tr>
                </thead>
                <tbody>
                  {processed.map((row, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="font-medium p-2">{row.education_label}</td>
                      <td>{Number(row.year_2018).toLocaleString("en-US")}</td>
                      <td>{Number(row.year_2024).toLocaleString("en-US")}</td>
                      <td className="font-semibold text-blue-700">
                        {row.growth}
                      </td>
                      <td className="font-semibold text-green-600">
                        {row.growthPct}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* ---- Bar Chart ---- */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[600px] w-full h-96 p-4">
              <Bar data={chartData} options={options} />
            </div>
          </div>
        </div>
      </div>

      {/* ---- PDF Button ---- */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdf} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
};

export default EngTable18;
